import type { StatusTone } from '@/shared/constants/statuses';

/**
 * A paleta dos cinco tons, num lugar só.
 *
 * **Ela morava em `features/calendar/lib/appearance.ts`**, e saiu de lá quando a
 * aba Início passou a desenhar gráfico: `shared/` não pode importar de
 * `features/` (ADR-006, e o ESLint recusa), então a alternativa seria o gráfico
 * escrever `bg-cyan-600` por conta própria. Duas paletas com os mesmos valores
 * não são uma duplicação inofensiva — são duas que divergem no primeiro ajuste,
 * e o sintoma é o pior possível: o "Em análise" do gráfico deixa de ser o mesmo
 * ciano do crachá ao lado, sem que nada quebre.
 *
 * ⚠️ **Isto é a paleta, não a REGRA.** O que cada tom significa continua em
 * `PROJECT_STATUS_TONES` ([statuses.ts](../constants/statuses.ts)); como o
 * calendário compõe chip a partir daqui continua em `appearance.ts`, junto do
 * argumento de por que preenchimento significa acesso. Este arquivo só responde
 * "que cor é o tom `info`" — e é o único que responde.
 *
 * **Por que classe do Tailwind e não valor hexadecimal:** os gráficos da Início
 * são `div` com altura em porcentagem, não `<svg>`. Foi escolha de desenho, e
 * ela cai bem aqui — em SVG a cor entraria por `fill-*`, que é outro conjunto de
 * classes, e a paleta teria de existir em duas formas para servir aos dois. Com
 * `div`, é a mesma classe que o crachá, o filete e a barra usam.
 */
interface ToneColors {
  /**
   * A cor cheia. É o filete da lista do dia, o ponto da folha de filtros **e** a
   * área de barra dos gráficos da Início.
   *
   * Um slot só para os três de propósito: são literalmente a mesma matiz na
   * mesma força, e separá-los seria criar o espaço onde elas divergem.
   */
  solid: string;
  /** O chip preenchido da grade — `access: 'full'`, o que abre inteiro. */
  chipFilled: string;
  /** O chip de contorno — `access: 'reduced'`, o que só se consulta. */
  chipOutline: string;
  /**
   * A área de fundo esmaecida, para o trilho de um gráfico ou o realce de um
   * período. Nunca carrega significado sozinha: é o que sobra da barra.
   */
  wash: string;
}

export const TONE_COLORS: Record<StatusTone, ToneColors> = {
  neutral: {
    solid: 'bg-slate-500',
    chipFilled: 'border-l-slate-500 bg-slate-500/10',
    chipOutline: 'border-slate-300 border-l-slate-400',
    wash: 'bg-slate-500/10',
  },
  info: {
    solid: 'bg-cyan-600',
    chipFilled: 'border-l-cyan-600 bg-cyan-500/10',
    chipOutline: 'border-cyan-200 border-l-cyan-500',
    wash: 'bg-cyan-500/10',
  },
  warning: {
    solid: 'bg-amber-500',
    chipFilled: 'border-l-amber-500 bg-amber-500/12',
    chipOutline: 'border-amber-200 border-l-amber-400',
    wash: 'bg-amber-500/12',
  },
  success: {
    solid: 'bg-emerald-600',
    chipFilled: 'border-l-emerald-600 bg-emerald-500/10',
    chipOutline: 'border-emerald-200 border-l-emerald-500',
    wash: 'bg-emerald-500/10',
  },
  /**
   * `danger` nunca chega à GRADE (ADR-015 §3: `rejected` e `cancelled` ficam
   * fora do calendário), mas chega ao gráfico de volume por mês da Início, que
   * olha para trás e mostra o que foi recusado. Ele está no mapa por ser um
   * `StatusTone`, e agora tem consumidor real.
   */
  danger: {
    solid: 'bg-rose-600',
    chipFilled: 'border-l-rose-600 bg-rose-500/10',
    chipOutline: 'border-rose-200 border-l-rose-500',
    wash: 'bg-rose-500/10',
  },
};
